import { useStore } from '@nanostores/preact'
import { useEffect, useState } from 'preact/hooks'
import { JSXInternal } from 'preact/src/jsx'

import { TranslationProvider } from './components/Translation'
import { WrapperHeader } from './components/WrapperHeader'
import { AlreadySubscribed } from './screens/AlreadySubscribedScreen'
import { SignIn } from './screens/SignInScreen'
import { SignUp } from './screens/SignUpScreen'
import { EmailVerify } from './screens/EmailVerifyScreen'
import { Loading } from './screens/LoadingScreen'
import { ErrorScreen } from './screens/ErrorScreen'
import { ForgotPassword } from './screens/ForgotPasswordScreen'
import { Payment } from './screens/PaymentScreen'
import { Success } from './screens/SuccessScreen'
import { getSupport } from './services/backend'
import { Screens } from './services/screens'
import { profile, support as supportStore } from './services/store'
import { Support } from './services/support'

import css from './style.module.scss'

const meta = document.querySelector('meta[name=csrf-token]') as HTMLMetaElement
const token = meta ? meta.content : ''

export function Modal({
  action,
  source,
  onClose,
  onReload
}: {
  action: Screens
  source: string
  onClose: () => void
  onReload: (action: Screens) => void
}) {
  const [errorMessage, setErrorMessage] = useState('')

  const [history, setHistory] = useState<Screens[]>([])
  const [page, setPage] = useState(Screens.LoadingScreen)
  const support = useStore(supportStore)

  const popHistory = () => {
    setHistory(history.slice(0, -1))
    setPage(history[history.length - 1])
  }

  const updatePage = (newPage: Screens) => {
    setHistory(history.concat(page))
    setPage(newPage)
  }

  useEffect(() => {
    setErrorMessage('')
    ;(async () => {
      let data: Support
      try {
        data = await getSupport()
      } catch (error) {
        let msg = 'fetching support, but the error is unknown'
        if (error instanceof Error) msg = error.message

        setPage(Screens.ErrorScreen)
        setErrorMessage(msg)

        return
      }

      supportStore.set(data)
      profile.setKey('source', source)
      profile.setKey('premium', data.user.premium)
      profile.setKey('authorized', data.user.signedIn)
      profile.setKey('countryIso', data.default_iso_country)
      profile.setKey('emailVerified', data.user.emailVerified)
      profile.setKey('email', data.user.email)
      // console.log(data)

      if (data.user.signedIn) {
        if (data.user.emailVerified) {
          setPage(Screens.PaymentScreen)
        } else {
          setPage(Screens.EmailVerifyScreen)
        }
      } else {
        setPage(Screens.SignUpScreen)
      }
    })()
  }, [action, source])

  const reloadPage = () => {
    if (action === Screens.ForgotPasswordScreen) {
      window.location.assign('/')
    } else {
      window.location.reload()
    }
  }
  const closePage = () => {
    if (profile.get().reloadRequired) {
      reloadPage()
    } else {
      onClose()
    }
  }

  let el: JSXInternal.Element
  switch (page) {
    case Screens.LoadingScreen:
      el = (
        <WrapperHeader onClose={closePage}>
          <Loading />
        </WrapperHeader>
      )
      break
    case Screens.ErrorScreen:
      el = (
        <WrapperHeader onClose={closePage}>
          <ErrorScreen message={errorMessage} onReload={reloadPage} />
        </WrapperHeader>
      )
      break
    case Screens.SignUpScreen:
      el = (
        <WrapperHeader onClose={closePage} tooltip="1/3">
          <SignUp
            token={token}
            isoCountries={support.iso_countries}
            onAuthenticate={() => updatePage(Screens.PaymentScreen)}
            onPageUpdate={updatePage}
          />
        </WrapperHeader>
      )
      break
    case Screens.SignInScreen:
      el = (
        <WrapperHeader onClose={closePage} onBack={popHistory} tooltip="1/3">
          <SignIn
            token={token}
            onAuthenticate={() => updatePage(Screens.PaymentScreen)}
            onPasswordForgot={() => updatePage(Screens.ForgotPasswordScreen)}
            onPageUpdate={updatePage}
          />
        </WrapperHeader>
      )
      break
    case Screens.ForgotPasswordScreen:
      el = (
        <WrapperHeader onBack={popHistory}>
          <ForgotPassword token={token} onPasswordReset={() => setPage(Screens.SignInScreen)} />
        </WrapperHeader>
      )
      break
    case Screens.AlreadySubscribedScreen:
      el = (
        <WrapperHeader onClose={closePage}>
          <AlreadySubscribed currencies={support.currencies} onClose={() => onReload(action)} />
        </WrapperHeader>
      )
      break
    case Screens.PaymentScreen:
      el = (
        <WrapperHeader
          onClose={closePage}
          onBack={popHistory}
          tooltip={
            history.includes(Screens.SignUpScreen) || history.includes(Screens.SignInScreen)
              ? '2/3'
              : '1/2'
          }
        >
          <Payment
            token={token}
            products={support.products}
            currencies={support.currencies}
            isoCountries={support.iso_countries}
            publishableKey={support.stripe_publishable_key}
            onPay={() => {
              if (profile.get().emailVerified) {
                updatePage(Screens.SuccessScreen)
              } else {
                updatePage(Screens.EmailVerifyScreen)
              }
            }}
          />
        </WrapperHeader>
      )
      break
    case Screens.EmailVerifyScreen:
      el = (
        <WrapperHeader onClose={closePage} onBack={history.length ? popHistory : undefined}>
          <EmailVerify token={token} onVerify={() => updatePage(Screens.SuccessScreen)} />
        </WrapperHeader>
      )
      break
    case Screens.SuccessScreen:
      el = (
        <WrapperHeader
          onClose={closePage}
          tooltip={
            history.includes(Screens.SignUpScreen) || history.includes(Screens.SignInScreen)
              ? '3/3'
              : '2/2'
          }
        >
          <Success onClose={closePage} />
        </WrapperHeader>
      )
      break
    default:
      throw new Error(`Unexpected error. Specified page (${page}) is not supported.`)
  }

  return (
    <TranslationProvider definitions={support?.messages}>
      <div
        onClick={(event) => {
          if ((event.target as HTMLElement) === (event.currentTarget as HTMLElement)) {
            closePage()
          }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            closePage()
          }
        }}
        role="button"
        tabIndex={0}
        className={css.backdrop}
      >
        {el}
      </div>
    </TranslationProvider>
  )
}
